import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react'
import { Link } from '@tanstack/react-router'

export type ButtonVariant = 'solid' | 'outline' | 'white'

const variants: Record<ButtonVariant, string> = {
  solid:
    'bg-royal text-white font-bold hover:border-royal-dark hover:bg-royal-dark',
  outline: 'bg-surface text-accent font-semibold hover:bg-surface-alt',
  white: 'bg-white text-royal font-bold hover:bg-alice',
}

export function buttonClass(variant: ButtonVariant = 'solid', extra = '') {
  return `inline-block rounded-sm border-2 border-royal px-4 py-2.5 text-center text-base leading-7 transition-colors max-md:block max-md:w-full ${variants[variant]} ${extra}`
}

type Props = { variant?: ButtonVariant } & (
  | ({ href: string } & AnchorHTMLAttributes<HTMLAnchorElement>)
  | ({ href?: undefined } & ButtonHTMLAttributes<HTMLButtonElement>)
)

/**
 * Renders a router `Link` for an internal "/..." href, an <a> for any other
 * href, and a <button> when there is none.
 */
export function Button({ variant = 'solid', className = '', ...props }: Props) {
  const cls = buttonClass(variant, className)
  if (props.href !== undefined) {
    const { href, ...anchor } = props
    if (href.startsWith('/') && anchor.target !== '_blank') {
      return <Link to={href} className={cls} {...anchor} />
    }
    return <a href={href} className={cls} {...anchor} />
  }
  return <button type="button" className={`cursor-pointer ${cls}`} {...props} />
}
